import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DataTable,
  DataTablePageEvent,
  DataTableRowClickEvent,
} from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { useInstitutionRankings } from "../../api/queries/institution-rankings/useInstitutionRankings";
import {
  InstitutionRankingListItem,
  rankValueOptions,
} from "../../api/types/institution-ranking";
import { useInstitutions } from "../../api/queries/institutions/useInstitutions";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { parseQueryString, toQueryString } from "../../common/utils";

export default function InstitutionRankingList() {
  const navigate = useNavigate();
  const limit = 20;
  const queryParams = parseQueryString(useLocation().search);

  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [sortField, setSortField] = useState(queryParams.sortField);
  const [sortOrder, setSortOrder] = useState(queryParams.sortOrder);

  const onPage = ({ first }: DataTablePageEvent) => setOffset(first);
  const onSort = (event: any) => {
    setSortField(event.sortField);
    setSortOrder(event.sortOrder);
  };

  // filters
  const { data: institutions } = useInstitutions({ limit: 100, offset: 0 }); // TODO
  const [fInstitutionId, setFInstitutionId] = useState<string>("");
  const onFInstitutionChange = (event: DropdownChangeEvent) =>
    setFInstitutionId(event.value);
  const fInstitutionIdElement = (
    <Dropdown
      options={institutions?.data.results || []}
      value={fInstitutionId}
      optionLabel="name"
      optionValue="id"
      showClear={true}
      onChange={onFInstitutionChange}
    />
  );

  const { data: institutionRanks, isLoading } = useInstitutionRankings({
    limit,
    offset,
    fInstitutionId,
  });

  const onRowClick = (event: DataTableRowClickEvent) =>
    navigate(`/institution-rankings/${event.data.id}`);

  // templates
  const tInstitution = (row: InstitutionRankingListItem) =>
    row.institution.name;

  const tRankValue = (row: InstitutionRankingListItem) => {
    const rankValueOption = rankValueOptions.find(
      (rankValueOption) => rankValueOption.value === row.rank_value,
    );
    return rankValueOption?.label || "";
  };

  const tNote = (row: InstitutionRankingListItem) => (
    <span dangerouslySetInnerHTML={{ __html: row.note }}></span>
  );

  useEffect(() => {
    const queryParamsStr = toQueryString({
      limit: 20,
      offset,
      sortField,
      sortOrder,
      fInstitutionId,
    });

    navigate({ search: queryParamsStr });
  }, [offset, navigate, sortField, sortOrder, fInstitutionId]);

  useEffect(() => {
    if (institutionRanks) {
      setTotal(institutionRanks.data.total_count);
    }
  }, [institutionRanks]);

  return (
    <div className={"col-12"}>
      <div className="flex py-3">
        <h1 className="m-0">Institution ranking</h1>

        <Button
          onClick={() => navigate("/institution-rankings/new")}
          className="p-button p-button-main p-button-sm ml-auto"
          label="Add new"
        />
      </div>

      <DataTable
        emptyMessage={"No records"}
        paginator={true}
        lazy={true}
        rows={20}
        first={offset}
        totalRecords={total}
        loading={isLoading}
        sortField={sortField}
        sortOrder={sortOrder}
        onPage={onPage}
        onSort={onSort}
        value={institutionRanks?.data.results || []}
        className="p-datatable-sm p-datatable-pointer"
        onRowClick={onRowClick}
        stripedRows={true}
        filterDisplay="row"
      >
        <Column
          header={"Institution"}
          body={tInstitution}
          filter={true}
          filterElement={fInstitutionIdElement}
          showFilterMenu={false}
        />

        <Column header={"Rank value"} body={tRankValue} />

        <Column header={"Note"} body={tNote} />
      </DataTable>
    </div>
  );
}
